import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import React, { forwardRef } from 'react'

const styles = {
  base: [
    // Base
    'relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border text-base/6 font-semibold',
    // Sizing
    'px-[calc(theme(spacing.3.5)-1px)] py-[calc(theme(spacing.2.5)-1px)] sm:px-[calc(theme(spacing.3)-1px)] sm:py-[calc(theme(spacing.1.5)-1px)] sm:text-sm/6',
    // Focus
    'focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
    // Disabled
    'disabled:opacity-50 disabled:cursor-not-allowed',
    // Icon
    '*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0',
  ],
  solid: [
    // Optical border, implemented as the button background to avoid corner artifacts
    'border-transparent',
    // Button background
    'bg-[#004d40]',
    // Dark mode: Subtle white outline is applied using a border
    'dark:border-white/5',
    // Hover state
    'hover:bg-[#00695c] active:bg-[#00695c]',
    // Text color
    'text-white',
    // Disabled
    'disabled:opacity-70',
  ],
  outline: [
    // Base
    'border-zinc-950/10 text-zinc-950 hover:bg-zinc-950/[2.5%] active:bg-zinc-950/[2.5%]',
    // Dark mode
    'dark:border-white/15 dark:text-white dark:hover:bg-white/5 dark:active:bg-white/5',
  ],
  ghost: [
    // Base
    'border-transparent text-zinc-950 hover:bg-zinc-950/5 active:bg-zinc-950/5',
    // Dark mode
    'dark:text-white dark:hover:bg-white/10 dark:active:bg-white/10',
  ],
  danger: [
    'bg-red-600 text-white border-red-700/90 hover:bg-red-500',
  ],
  success: [
    'bg-green-600 text-white border-green-700/90 hover:bg-green-500',
  ],
  colors: {
    primary: [
      'bg-[#004d40] text-white border-[#005d50]/90 hover:bg-[#00695c]',
    ],
    secondary: [
      'bg-zinc-600 text-white border-zinc-700/90 hover:bg-zinc-500',
    ],
    blue: [
      'bg-blue-600 text-white border-blue-700/90 hover:bg-blue-500',
    ],
  },
  sizes: {
    xs: 'px-2.5 py-1.5 text-xs',
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-5 py-2.5 text-lg',
    xl: 'px-6 py-3 text-xl',
  }
};

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
function TouchTarget({ children }) {
  return (
    <>
      <span
        className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  )
}

// Button component with API compatibility to the original implementation
export const Button = ({ 
  children,
  className = '',
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled = false,
  icon = null,
  iconPosition = 'left',
  as: Component = 'button',
  ...props
}) => {
  // Map variant to appropriate style
  let buttonStyle;
  if (variant === 'primary') {
    buttonStyle = clsx(styles.solid, styles.colors.primary);
  } else if (variant === 'secondary') {
    buttonStyle = clsx(styles.solid, styles.colors.secondary);
  } else if (variant === 'outline') {
    buttonStyle = styles.outline;
  } else if (variant === 'ghost') {
    buttonStyle = styles.ghost;
  } else if (variant === 'danger') {
    buttonStyle = clsx(styles.solid, styles.danger);
  } else if (variant === 'success') {
    buttonStyle = clsx(styles.solid, styles.success);
  } else if (variant === 'blue') {
    buttonStyle = clsx(styles.solid, styles.colors.blue);
  } else {
    buttonStyle = clsx(styles.solid, styles.colors.primary);
  }

  // Combine all classes
  const classes = clsx(
    styles.base,
    buttonStyle,
    styles.sizes[size],
    fullWidth && 'w-full',
    className
  );

  return (
    <Component
      className={classes}
      disabled={disabled || loading}
      {...props}
    >
      <TouchTarget>
        {icon && iconPosition === 'left' && <span className="mr-2">{icon}</span>}
        {loading ? 'Loading...' : children}
        {icon && iconPosition === 'right' && <span className="ml-2">{icon}</span>}
      </TouchTarget>
    </Component>
  );
};

// Predefined button variants
export const Primary = (props) => <Button variant="primary" {...props} />;
export const Secondary = (props) => <Button variant="secondary" {...props} />;
export const Outline = (props) => <Button variant="outline" {...props} />;
export const Ghost = (props) => <Button variant="ghost" {...props} />;
export const Danger = (props) => <Button variant="danger" {...props} />;
export const Success = (props) => <Button variant="success" {...props} />;
export const Blue = (props) => <Button variant="blue" {...props} />;
